import { RefundResult } from "api/responses";
import type { RefundModalEvent, RefundResultEvent } from "./refundModal.events";
import type { RefundResultState, TransactionRefundModalState } from "./refundModal.state";

/**
 * Determine the next transaction/refund state based on a new event.
 * @param state The existing transaction/refund state.
 * @param action The new event that has occurred.
 * @returns The new transaction/refund state.
 */
export function reducer(state: TransactionRefundModalState, action: RefundModalEvent): TransactionRefundModalState {
    switch (action.type) {
        case "confirm-refund":
            return reduceConfirmRefund(state);

        case "refund-result":
            return reduceRefundResult(state, action);

        case "cancel":
            return { type: "transaction-selected", transactionId: state.transactionId };
    }
}

function reduceConfirmRefund(state: TransactionRefundModalState): TransactionRefundModalState {
    if (state.type === "transaction-selected" || state.type === "refund-result") {
        return { type: "refund-processing", transactionId: state.transactionId };
    } else {
        // This action should never occur with other states--reset to the initial state.
        return { type: "transaction-selected", transactionId: state.transactionId };
    }
}

function reduceRefundResult(
    state: TransactionRefundModalState,
    action: RefundResultEvent,
): TransactionRefundModalState {
    if (state.type === "refund-processing" || state.type === "refund-polling") {
        return (
            // Make the result state, even if the refund is only pending. We don't want to sit at a spinner for minutes
            // on end if the refund takes a while to process.
            {
                ...makeResultState(action),
                transactionId: state.transactionId,
            }
        );
    } else {
        // This action should never occur with other states--reset to the initial state.
        return { type: "transaction-selected", transactionId: state.transactionId };
    }
}

const defaultErrorMessage = "There was a problem refunding this transaction. Contact support if this error persists.";

/** Helper to transform raw API response data to a refund result state. */
function makeResultState(trr: RefundResultEvent) {
    const success = trr.success;
    // if the refund is successful or pending, we want to show the result
    const result = success || trr.pending ? trr.result : undefined;
    const title =
        result === RefundResult.Returned
            ? "Refund Successful"
            : result === RefundResult.Accepted
              ? "Refund Pending"
              : result === RefundResult.Voided
                ? "Transaction Voided"
                : "Refund Unsuccessful";

    return {
        type: "refund-result",
        status: result === RefundResult.Accepted ? "info" : success ? "success" : "error",
        title,
        message: trr.message ?? (success ? undefined : defaultErrorMessage),
        cardBrand: trr.cardBrand,
        last4: trr.last4,
        amount: trr.amount,
    } as const satisfies Partial<RefundResultState>;
}
